import React, { useContext, useReducer, ReactNode } from 'react'
import { uniqueId } from 'lodash'

enum Actions {
  NEIGHBORHOODS,
  WORKPLACEGROUPS,
  EMPLOYEE_CATALOG,
  BUILDINGS,
  APP_DATA,
  DEPARTMENTS,
  BOOKINGS,
  BUILDING_TYPES,
  SPACE_TYPES,
  LOCATION_CAPACITIES,
  PROJECTIONS,
  LOCATION_HEALTH_CHECKS,
  LOCATION_BOOKABLE_DESKS,
  PORTFOLIO,
}

type RootAction = { type: Actions }
interface ReloadState {
  neighborhoods: string
  workplaceGroups: string
  employeeCatalog: string
  buildings: string
  appData: string
  departments: string
  bookings: string
  buildingTypes: string
  spaceTypes: string
  locationCapacities: string
  projections: string
  locationHealthChecks: string
  locationBookableDesks: string
  portfolio: string
}

const initState = {
  neighborhoods: '',
  workplaceGroups: '',
  employeeCatalog: '',
  buildings: '',
  appData: '',
  departments: '',
  bookings: '',
  buildingTypes: '',
  spaceTypes: '',
  locationCapacities: '',
  projections: '',
  locationHealthChecks: '',
  locationBookableDesks: '',
  portfolio: '',
}

interface IReloadContext {
  neighborhoods: () => void
  workplaceGroups: () => void
  employeeCatalog: () => void
  buildings: () => void
  appData: () => void
  departments: () => void
  bookings: () => void
  buildingTypes: () => void
  spaceTypes: () => void
  locationCapacities: () => void
  projections: () => void
  locationHealthChecks: () => void
  locationBookableDesks: () => void
  portfolio: () => void
}

const ShouldReloadContext = React.createContext<ReloadState>(initState)
const ReloadContext = React.createContext<IReloadContext | undefined>(undefined)

function ReloadReducer(state: ReloadState, action: RootAction) {
  const id = uniqueId()

  switch (action.type) {
    case Actions.NEIGHBORHOODS:
      return { ...state, neighborhoods: id }

    case Actions.WORKPLACEGROUPS:
      return { ...state, workplaceGroups: id }

    case Actions.EMPLOYEE_CATALOG:
      return { ...state, employeeCatalog: id }

    case Actions.BUILDINGS:
      return { ...state, buildings: id }

    case Actions.APP_DATA:
      return { ...state, appData: id }

    case Actions.DEPARTMENTS:
      return { ...state, departments: id }

    case Actions.BOOKINGS:
      return { ...state, bookings: id }

    case Actions.BUILDING_TYPES:
      return { ...state, buildingTypes: id }

    case Actions.SPACE_TYPES:
      return { ...state, spaceTypes: id }

    case Actions.PROJECTIONS:
      return { ...state, projections: id }

    case Actions.LOCATION_CAPACITIES:
      return { ...state, locationCapacities: id }

    case Actions.LOCATION_HEALTH_CHECKS:
      return { ...state, locationHealthChecks: id }

    case Actions.LOCATION_BOOKABLE_DESKS:
      return { ...state, locationBookableDesks: id }

    case Actions.PORTFOLIO:
      return { ...state, portfolio: id }

    default: {
      throw new Error(`Unhandled action type: ${action}`)
    }
  }
}

function ReloadContextProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(ReloadReducer, initState)

  return (
    <ShouldReloadContext.Provider value={state}>
      <ReloadContext.Provider
        value={{
          neighborhoods: () => dispatch({ type: Actions.NEIGHBORHOODS }),
          workplaceGroups: () => dispatch({ type: Actions.WORKPLACEGROUPS }),
          employeeCatalog: () => dispatch({ type: Actions.EMPLOYEE_CATALOG }),
          buildings: () => dispatch({ type: Actions.BUILDINGS }),
          buildingTypes: () => dispatch({ type: Actions.BUILDING_TYPES }),
          appData: () => dispatch({ type: Actions.APP_DATA }),
          departments: () => dispatch({ type: Actions.DEPARTMENTS }),
          bookings: () => dispatch({ type: Actions.BOOKINGS }),
          locationCapacities: () => dispatch({ type: Actions.LOCATION_CAPACITIES }),
          projections: () => dispatch({ type: Actions.PROJECTIONS }),
          spaceTypes: () => dispatch({ type: Actions.SPACE_TYPES }),
          locationHealthChecks: () => dispatch({ type: Actions.LOCATION_HEALTH_CHECKS }),
          locationBookableDesks: () => dispatch({ type: Actions.LOCATION_BOOKABLE_DESKS }),
          portfolio: () => dispatch({ type: Actions.PORTFOLIO }),
        }}
      >
        {children}
      </ReloadContext.Provider>
    </ShouldReloadContext.Provider>
  )
}

function useShouldReload(): ReloadState {
  const context = useContext(ShouldReloadContext)
  if (context === undefined) {
    throw new Error('useShouldReload must be used within a CountProvider')
  }
  return context
}

function useReload() {
  const context = useContext(ReloadContext)
  if (context === undefined) {
    throw new Error('useReload must be used within a CountProvider')
  }
  return context
}

export { ReloadContextProvider, useReload, useShouldReload }
